import { msg, t } from '@lingui/core/macro';
import type { ICarImageElement } from 'data/schema';
import { confirmDialogState, hideConfirmDialog, showConfirmDialog } from 'state/confirm-dialog';
import { userSession } from 'state/session';
import type { IPublicSession } from 'state/session';
import { showToast } from 'state/toast';
import { user } from 'utils/auth/user';
import { getImages } from 'utils/network/getCarImages';
import { tenantConfig } from 'utils/tenant';
import type { SessionState } from './computed';

/**
 * AVS-4460 part 2: what happens once a joining customer's 3D stream has failed on capacity four
 * times (`isCapacityCutoff` in `utils/ui/capacityRetry.ts`).
 *
 * - The customer is asked whether to keep trying or switch to 2D (`promptCapacityCutoff`).
 * - The customer writes the customer-owned `customer3DUnavailable` session flag
 *   (`setCustomer3DUnavailable`), the only customer→dealer channel there is.
 * - The dealer gets a warning toast when that flag turns true (`initCustomer3DUnavailableNotice`).
 * - The joiner's 2D fallback gets its own images when the shared gallery has none
 *   (`capacityFallbackGallery`, `fetchCapacityFallbackImages`).
 */

/** Stable id for alert filtering and for telling our dialog apart from a foreign one. */
export const CAPACITY_CUTOFF_DIALOG_ID = 'DIALOG_3D_CAPACITY_CUTOFF';

/** Stable id so the toast dedup (state/toast.ts) never stacks two of these. */
export const CUSTOMER_3D_UNAVAILABLE_TOAST_ID = 'CUSTOMER_3D_UNAVAILABLE';

/** `busy`: another dialog was active, so nothing was opened. Ask again on the next failure. */
export type TCapacityCutoffAnswer = 'keep' | 'switch' | 'busy';

/**
 * Asks the customer whether to keep waiting for 3D or continue with images.
 *
 * `showConfirmDialog` resolves `false` for a human "Switch to 2D", but ALSO when no host ever
 * claims the dialog (settle timeout), when an interceptor prevents it, or when a later request
 * resets a never-rendered dialog. Only a dialog that was actually rendered can carry a human
 * answer, so a `false` without a render maps to `keep`: the retries continue, which is the safe
 * default. A filter-suppressed dialog auto-confirms, which also maps to `keep`.
 *
 * Closing the dialog via `closeCapacityCutoffPrompt` never settles this promise (by design);
 * callers must not await it in a chain that needs to finish.
 */
export async function promptCapacityCutoff(): Promise<TCapacityCutoffAnswer> {
  if (confirmDialogState.get().status !== 'idle') {
    return 'busy';
  }

  // `sawRender` tracks the live claim, not "was ever claimed": a host that unmounts releases its
  // claim, and a later request then resets our open dialog as cancelled with nobody answering.
  // The watcher stops itself once our dialog is gone, because a close does not settle the await.
  let sawRender = false;
  let opened = false;
  const stopWatching = confirmDialogState.on('change', () => {
    const dialog = confirmDialogState.get();
    if (dialog.dialogId === CAPACITY_CUTOFF_DIALOG_ID && dialog.isOpen) {
      opened = true;
      sawRender = dialog.isBeingRendered === true;
    } else if (opened) {
      stopWatching();
    }
  });

  const confirmed = await showConfirmDialog({
    dialogId: CAPACITY_CUTOFF_DIALOG_ID,
    title: t`3D view not available`,
    message: t`We can't connect you to the 3D view right now. You can keep waiting or continue with images.`,
    confirmText: t`Keep trying`,
    cancelText: t`Switch to 2D`,
  });
  stopWatching();

  return !confirmed && sawRender ? 'switch' : 'keep';
}

/** Hides the cutoff dialog if it is the one showing. A foreign dialog is left alone. */
export function closeCapacityCutoffPrompt(): void {
  if (confirmDialogState.get().dialogId === CAPACITY_CUTOFF_DIALOG_ID) {
    hideConfirmDialog();
  }
}

/** Writes the customer-owned flag. Only a customer writes it, and only when the value changes. */
export function setCustomer3DUnavailable(value: boolean): void {
  if (user.type !== 'customer') {
    return;
  }
  if (userSession().get('customer3DUnavailable') === value) {
    return;
  }
  userSession().setValues({ customer3DUnavailable: value });
}

/**
 * Dealer-side warning toast when a customer reports that 3D is unavailable.
 *
 * Edge-triggered on the change to `true` (positional `(changedKeys, changedValues, oldValues)`
 * contract), so a broadcast that already carries `true` never toasts again. A change set that
 * also changes `id` is a session load (e.g. a dealer reload), not a live report: no toast.
 * Session analytics for the same flag live in `state/analytics.ts`.
 */
export function initCustomer3DUnavailableNotice(state: SessionState): void {
  state.on('change', (changedKeys, changedValues, oldValues) => {
    const index = changedKeys.indexOf('customer3DUnavailable');
    if (index === -1 || changedValues[index] !== true || oldValues[index] === true) {
      return;
    }
    if (!user.isDealer || changedKeys.includes('id')) {
      return;
    }
    showToast({
      type: 'warn',
      notificationId: CUSTOMER_3D_UNAVAILABLE_TOAST_ID,
      title: msg`Customer can't see 3D`,
      message: msg`The customer could not connect to the 3D stream.`,
    });
  });
}

type TFallbackCar = Pick<IPublicSession, 'prString' | 'vinOrMarketingCode'>;

/** Images the joiner fetched for its own 2D fallback, keyed to the car they show. */
export interface ICapacityFallbackImages {
  carKey: string;
  items: ICarImageElement[];
}

/** What the 2D fallback renders. `usesLocalImages` is true when the fallback needs its own copy. */
export interface ICapacityFallbackGallery {
  usesLocalImages: boolean;
  status: IPublicSession['carImagesStatus'];
  items: ICarImageElement[] | undefined;
}

/** Both ids a car's images can be keyed by: GMS brands use `vinOrMarketingCode`, Audi `prString`. */
export function capacityFallbackCarKey({ prString, vinOrMarketingCode }: TFallbackCar): string {
  return `${prString ?? ''}|${vinOrMarketingCode ?? ''}`;
}

/**
 * The gallery a joiner's 2D fallback shows while its 3D waits for capacity.
 *
 * Nobody fills the shared gallery in a 3D session: the dealer fetches images only in 2D or
 * comparison mode, and `showImages` declines in 3D. So unless the shared gallery is ready for
 * this car, the fallback shows its own copy. Outside the fallback it passes the shared one through.
 */
export function capacityFallbackGallery(
  session: TFallbackCar & Pick<IPublicSession, 'carImages' | 'carImagesStatus'>,
  waitingForCapacity: boolean,
  local?: ICapacityFallbackImages
): ICapacityFallbackGallery {
  const sharedReady = session.carImagesStatus === 'ready' && session.carImages?.prString === session.prString;
  if (!waitingForCapacity || sharedReady || !tenantConfig().isAllowed('2D')) {
    return { usesLocalImages: false, status: session.carImagesStatus, items: session.carImages?.items };
  }
  if (local?.carKey !== capacityFallbackCarKey(session)) {
    return { usesLocalImages: true, status: 'loading', items: undefined };
  }
  return local.items.length
    ? { usesLocalImages: true, status: 'ready', items: local.items }
    : { usesLocalImages: true, status: 'unset', items: undefined };
}

let pendingFallbackImages: { carKey: string; request: Promise<ICapacityFallbackImages> } | undefined;

/**
 * Fetches the current car's images for the 2D fallback. It writes nothing: `carImages` and
 * `carImagesStatus` are shared, and the dealer is still in 3D. One request per car at a time,
 * because the fallback unmounts and remounts on every retry of the deck. `getImages` never
 * throws: a failed request answers an empty list.
 */
export function fetchCapacityFallbackImages(): Promise<ICapacityFallbackImages> {
  const session = userSession().get();
  const carKey = capacityFallbackCarKey(session);
  if (pendingFallbackImages?.carKey === carKey) {
    return pendingFallbackImages.request;
  }
  const request = getImages({
    prString: session.prString,
    brand: session.brand!,
    market: session.market!,
    vinOrMarketingCode: session.vinOrMarketingCode,
  })
    .then(({ items = [] }) => ({ carKey, items }))
    .finally(() => {
      if (pendingFallbackImages?.request === request) pendingFallbackImages = undefined;
    });
  pendingFallbackImages = { carKey, request };
  return request;
}
